import { v4 as uuidv4 } from 'uuid';
import { BunkerRowFormState, StateField } from '@navstation-portal/reporting/data-model/vessel-report';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { signalStoreFeature } from '@ngrx/signals';
import { on, withReducer } from '@ngrx/signals/events';
import { initStateField } from '../../../offshore-report-utils/store.utility';
import { BunkerBuilderState } from './offshore-bunker.store';
import { offshoreBunkerEvents } from './with-offshore-bunker-events';

export function withOffshoreBunkerReducer() {
  return signalStoreFeature(
    withReducer(
      on(offshoreBunkerEvents.updateBunkerRow, (payload, state) => {
        const { rowIndex, density, lcv, sulphur, viscosity } = payload.payload;
        const bunkerState = state as BunkerBuilderState;
        const bunkerRows: BunkerRowFormState[] = bunkerState?.fuelOilBunkerTable?.bunkerRows || [];
        const bunkerRowToUpdate = bunkerRows[rowIndex] as BunkerRowFormState;
        if (!bunkerRowToUpdate) return state;

        if (density === undefined && lcv === undefined && sulphur === undefined && viscosity === undefined) {
          return state;
        }

        const updatedBunkerRow: BunkerRowFormState = {
          ...bunkerRowToUpdate,
          density: {
            ...bunkerRowToUpdate?.density,
            value: density,
          },
          lcv: {
            ...bunkerRowToUpdate?.lcv,
            value: lcv,
          },
          sulphur: {
            ...bunkerRowToUpdate?.sulphur,
            value: sulphur,
          },
          viscosity: {
            ...bunkerRowToUpdate?.viscosity,
            value: viscosity,
          },
        };

        const updatedBunkerRows = [...bunkerRows];
        updatedBunkerRows[rowIndex] = updatedBunkerRow;

        return {
          ...state,
          fuelOilBunkerTable: {
            ...bunkerState.fuelOilBunkerTable,
            bunkerRows: updatedBunkerRows,
          },
        };
      }),
      on(offshoreBunkerEvents.removeBunkerRow, (payload, state) => {
        const { rowIndex } = payload.payload;
        const bunkerState = state as BunkerBuilderState;
        const bunkerRows: BunkerRowFormState[] = bunkerState?.fuelOilBunkerTable?.bunkerRows || [];
        const updatedBunkerRows = bunkerRows.filter((_, index) => index !== rowIndex);
        return {
          ...state,
          fuelOilBunkerTable: {
            ...bunkerState.fuelOilBunkerTable,
            bunkerRows: updatedBunkerRows,
          },
        };
      }),
      on(offshoreBunkerEvents.addBunkerRow, (payload, state) => {
        const bunkerState = state as BunkerBuilderState;
        const bunkerRows: BunkerRowFormState[] = bunkerState?.fuelOilBunkerTable?.bunkerRows || [];
        const newBunkerRow: BunkerRowFormState = {
          rowId: initStateField<string>(uuidv4()),
          fuelType: initStateField<SelectOption>(),
          quantityOrdered: initStateField<number>(),
          quantityReceived: initStateField<number>(),
          bdnFigures: initStateField<string>(),
          isoFuelGrade: initStateField<SelectOption>(),
          density: initStateField<number>(),
          lcv: initStateField<number>(),
          sulphur: initStateField<number>(),
          viscosity: initStateField<number>(),
          labSulphur: initStateField<number>(),
          stableUntil: initStateField<Date>(),
        };
        return {
          ...state,
          fuelOilBunkerTable: {
            ...bunkerState.fuelOilBunkerTable,
            bunkerRows: [...bunkerRows, newBunkerRow],
          },
        };
      }),
      on(offshoreBunkerEvents.clearBunkerRow, (payload, state) => {
        const { rowIndex, omitFields } = payload.payload;
        const bunkerState = state as BunkerBuilderState;
        const bunkerRows: BunkerRowFormState[] = bunkerState?.fuelOilBunkerTable?.bunkerRows || [];
        const bunkerRowToClear = bunkerRows[rowIndex] as BunkerRowFormState;
        if (!bunkerRowToClear) return state;
        const fieldsToOmit = omitFields || [];
        const clearedBunkerRow: BunkerRowFormState = {
          ...bunkerRowToClear,
          fuelType: fieldsToOmit.includes('fuelType')
            ? bunkerRowToClear.fuelType
            : resetFieldState(bunkerRowToClear.fuelType),
          isoFuelGrade: fieldsToOmit.includes('isoFuelGrade')
            ? bunkerRowToClear.isoFuelGrade
            : resetFieldState(bunkerRowToClear.isoFuelGrade),
          quantityOrdered: fieldsToOmit.includes('quantityOrdered')
            ? bunkerRowToClear.quantityOrdered
            : resetFieldState(bunkerRowToClear.quantityOrdered),
          quantityReceived: fieldsToOmit.includes('quantityReceived')
            ? bunkerRowToClear.quantityReceived
            : resetFieldState(bunkerRowToClear.quantityReceived),
          bdnFigures: fieldsToOmit.includes('bdnFigures')
            ? bunkerRowToClear.bdnFigures
            : resetFieldState(bunkerRowToClear.bdnFigures),
          density: fieldsToOmit.includes('density')
            ? bunkerRowToClear.density
            : resetFieldState(bunkerRowToClear.density),
          lcv: fieldsToOmit.includes('lcv') ? bunkerRowToClear.lcv : resetFieldState(bunkerRowToClear.lcv),
          sulphur: fieldsToOmit.includes('sulphur')
            ? bunkerRowToClear.sulphur
            : resetFieldState(bunkerRowToClear.sulphur),
          viscosity: fieldsToOmit.includes('viscosity')
            ? bunkerRowToClear.viscosity
            : resetFieldState(bunkerRowToClear.viscosity),
          labSulphur: fieldsToOmit.includes('labSulphur')
            ? bunkerRowToClear.labSulphur
            : resetFieldState(bunkerRowToClear.labSulphur),
          stableUntil: fieldsToOmit.includes('stableUntil')
            ? bunkerRowToClear.stableUntil
            : resetFieldState(bunkerRowToClear.stableUntil),
        };

        const updatedBunkerRows = [...bunkerRows];
        updatedBunkerRows[rowIndex] = clearedBunkerRow;

        return {
          ...state,
          fuelOilBunkerTable: {
            ...bunkerState.fuelOilBunkerTable,
            bunkerRows: updatedBunkerRows,
          },
        };
      })
    )
  );
}

const resetFieldState = <T>(field: StateField<T>) => {
  const clearedState: StateField<T> = {
    ...field,
    value: undefined as T,
    touched: false,
    pristine: true,
    status: 'PENDING',
  };
  return clearedState;
};
